/* eslint-disable import/no-cycle */
import { GraphQLClient } from 'graphql-request'
import { getClientHeaders } from '../../config/index'
import { UserToken } from '../../types'
import { trigger } from '../events'
import Announcement from './announcement'
import Events from './events'
import Experiment from './experiment'
import Languages from './languages'
import Playlist from './playlist'
import ReferralLink from './referral-link'
import Subscription from './subscription'
import Task from './task'
import User from './user'

export type MoisesCli = ReturnType<typeof Languages> &
  ReturnType<typeof Playlist> &
  ReturnType<typeof Announcement> &
  ReturnType<typeof ReferralLink> &
  ReturnType<typeof Task> &
  ReturnType<typeof Events> &
  ReturnType<typeof Experiment> &
  ReturnType<typeof Subscription> &
  ReturnType<typeof User> & {
    session: {
      authToken: UserToken
      apiEndpoint: string
    }
    auth: (authToken: UserToken) => void
    isAuthenticated: () => boolean
    graphQL: ({
      query,
      variables
    }: {
      query: string
      variables?: any
    }) => Promise<any>
  }

export const Moises = (config: any): MoisesCli => {
  const session = {
    authToken: config.authToken || process.env.MOISES_AUTH_TOKEN,
    apiEndpoint: config.apiEndpoint || 'https://api.moises.ai'
  }

  const graphQLClient = new GraphQLClient(`${session.apiEndpoint}/graphql`, {
    credentials: 'include',
    mode: 'cors'
  })

  function auth(authToken: string | UserToken): void {
    session.authToken = authToken
  }

  function isAuthenticated(): boolean {
    return !!session.authToken
  }

  async function graphQL({
    query,
    variables
  }: {
    query: string
    variables?: any
  }): Promise<any> {
    if (!session.authToken) {
      throw new Error('You need to set your apiKey')
    }

    try {
      const response = await graphQLClient.request(query, variables, {
        Authorization: session.authToken,
        ...getClientHeaders()
      })

      return response
    } catch (e: any) {
      const isAuthFailed = e?.response?.errors?.some(
        (i: any) => i?.extensions?.code === 'UNAUTHENTICATED'
      )

      if (isAuthFailed) {
        trigger('firebase:refresh-token')
      }

      console.log('UNAUTHENTICATED?', isAuthFailed) // eslint-disable-line no-console
      console.error('Error fetching graphql') // eslint-disable-line no-console
      throw new Error(e)
    }
  }

  const user = User(graphQL)
  const playlist = Playlist(graphQL)
  const announcement = Announcement(graphQL)
  const languages = Languages(graphQL)
  const referralLink = ReferralLink(graphQL)
  const events = Events(graphQL)
  const task = Task(graphQL)
  const experiment = Experiment(graphQL)
  const subscription = Subscription(graphQL)

  return {
    ...user,
    ...playlist,
    ...languages,
    ...announcement,
    ...referralLink,
    ...task,
    ...events,
    ...experiment,
    ...subscription,
    session,
    auth,
    isAuthenticated,
    graphQL
  }
}
